import { useState, useCallback } from 'react';
import { fetchOpuSession, fetchOpuDonors, saveOpuSession } from '../data/opu';
import { getPending } from '../data/local/pending';

/**
 * Hook para persistência da sessão de OPU nas tabelas normalizadas.
 * Retorna e salva dados no mesmo shape que service.opu_data usava.
 */
export const useOPUSession = (laboratoryId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Carrega sessão OPU de um serviço.
   * Retorna null se não existir.
   */
  const loadOPUData = useCallback(async (serviceId) => {
    if (!serviceId || !laboratoryId) return null;
    setLoading(true);
    setError(null);
    try {
      // O que foi lançado offline é mais novo que qualquer coisa no servidor.
      const pendente = await getPending('opu', serviceId);
      if (pendente) return pendente;

      const session = await fetchOpuSession(serviceId);
      if (!session) return null;

      const donors = await fetchOpuDonors(session.id);

      // Retorna no mesmo shape do JSON antigo para compatibilidade com os componentes
      return {
        _session_id: session.id,
        session: {
          date: session.session_date,
          time: session.session_time,
          farm: session.farm,
          technician_name: session.technician_name,
          protocol: session.protocol
        },
        donors: (donors || []).map(d => ({
          _db_id: d.id,
          id: d.id,
          animal_id: d.animal_id,
          animal_identifier: d.animal_identifier,
          rgd: d.rgd,
          breed: d.breed,
          bull: d.bull_name,
          aspiration_time: d.aspiration_time,
          oocytes_recovered: d.oocytes_recovered,
          oocytes_viable: d.oocytes_viable,
          oocytes_denuded: d.oocytes_denuded,
          recovery_rate: d.recovery_rate,
          observations: d.observations
        })),
        general_observations: session.general_observations,
        completed: session.completed,
        completed_at: session.completed_at
      };
    } catch (err) {
      console.error('useOPUSession.loadOPUData:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [laboratoryId]);

  /**
   * Salva sessão OPU (upsert session + replace donors).
   * @param {string} serviceId
   * @param {Object} opuData - same shape as opu_data JSON
   * @returns {string|null} opu_session id
   */
  const saveOPUData = useCallback(async (serviceId, opuData) => {
    if (!serviceId || !laboratoryId || !opuData) return null;
    setLoading(true);
    setError(null);
    try {
      const { session, donors = [], general_observations, completed, completed_at } = opuData;

      // Upsert opu_session
      const sessionPayload = {
        service_id: serviceId,
        laboratory_id: laboratoryId,
        session_date: session?.date || null,
        session_time: session?.time || null,
        farm: session?.farm || null,
        technician_name: session?.technician_name || null,
        protocol: session?.protocol || null,
        general_observations: general_observations || null,
        completed: completed || false,
        completed_at: completed ? (completed_at || new Date().toISOString()) : null
      };

      // Sem `opu_session_id`: quem preenche é a operação, que só conhece o id
      // no momento em que realmente roda — pode ser agora ou depois, na fila.
      const donorsPayload = donors.map((d, idx) => ({
        laboratory_id: laboratoryId,
        sort_order: idx,
        animal_id: d.animal_id || null,
        animal_identifier: d.animal_identifier || null,
        rgd: d.rgd || null,
        breed: d.breed || null,
        bull_name: d.bull || d.bull_name || null,
        aspiration_time: d.aspiration_time || null,
        oocytes_recovered: parseInt(d.oocytes_recovered) || 0,
        oocytes_viable: parseInt(d.oocytes_viable) || 0,
        oocytes_denuded: parseInt(d.oocytes_denuded) || 0,
        recovery_rate: d.recovery_rate || null,
        observations: d.observations || null
      }));

      return await saveOpuSession(sessionPayload, donorsPayload, opuData);
    } catch (err) {
      console.error('useOPUSession.saveOPUData:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [laboratoryId]);

  return { loading, error, loadOPUData, saveOPUData };
};
